import { useState } from 'react';
import { createPryme } from '@pryme/sdk';
import { api, Alert, ErrorAlert, Spinner, useAsyncAction } from '@pryme/ui';
import { matchSecret, prymeKeysOf } from '../lib/signing';
import { activationSnippet, snippetValues } from '../lib/snippets';
import { canDevelop, type Service } from '../lib/types';
import { CodeBlock } from './CodeBlock';
import { ErrorBox } from './ErrorBox';
import { SecretKeyInput } from './SecretKeyInput';
import { apiOrigin } from '../lib/portal';

export function activationBlockers(s: Service): string[] {
  const out: string[] = [];
  if (s.status === 'pending_credentials') out.push('Generate credentials.');
  if (s.redirectUris.length === 0) out.push('Register at least one redirect URI.');
  if (s.environment === 'production' && s.redirectUris.length > 0 && !s.domain) out.push('Put your redirect URIs on a verified domain of your organization (Domains).');
  return out;
}

/**
 * Signed activation: Pryme issues a challenge; your backend proves it holds the secret signing key by signing
 * POST /v1/services/activate. Here it can be done in-browser (memory-only key) or via Node.
 */
export function ActivationPanel(props: { service: Service; initialSecret?: string; onActivated(): void }) {
  const s = props.service;
  const [challenge, setChallenge] = useState<string | null>(null);
  const [secret, setSecret] = useState(props.initialSecret ?? '');
  const [result, setResult] = useState<{ serviceId: string; status: string } | null>(null);
  const get = useAsyncAction();
  const act = useAsyncAction();

  if (s.status === 'active') {
    return <Alert tone="ok">This service is active. It accepts signed requests and “Continue with Pryme” sign-ins.</Alert>;
  }
  if (s.status === 'suspended' || s.status === 'revoked') return <Alert tone="bad">This service is {s.status}. It accepts no requests or sign-ins.</Alert>;

  const blockers = activationBlockers(s);
  const match = secret.trim() ? matchSecret(secret, s.keys) : null;

  const activate = async () => {
    if (!challenge || !match?.ok) return;
    const pryme = createPryme({
      apiBase: apiOrigin(),
      serviceId: s.id,
      apiAudience: s.apiAudience,
      signingKey: { keyId: match.key.id, secretKey: match.signingSecret },
      prymeKeys: prymeKeysOf(s, match.key),
      environment: s.environment,
    });
    const r = await act.run(() => pryme.activate(challenge));
    if (r) {
      setResult(r);
      props.onActivated();
    }
  };

  return (
    <div>
      {blockers.length > 0 ? (
        <Alert tone="warn">
          <strong>Before activation</strong>
          <ul style={{ margin: '0.25rem 0 0', paddingLeft: '1.2rem' }}>{blockers.map((b) => <li key={b}>{b}</li>)}</ul>
        </Alert>
      ) : (
        <p className="p-small p-muted">Activation proves that your backend holds the secret signing key: Pryme issues a one-time challenge, and you return it in a request signed with it.</p>
      )}

      {!challenge ? (
        <button
          type="button"
          className="p-btn"
          disabled={get.busy || s.status !== 'pending_activation' || !canDevelop(s.role)}
          onClick={async () => {
            const r = await get.run(() => api<{ challenge: string }>(`/v1/services/${s.id}/activation/challenge`, { method: 'POST' }));
            if (r) setChallenge(r.challenge);
          }}
        >
          {get.busy ? <Spinner label="Requesting…" /> : 'Get activation challenge'}
        </button>
      ) : (
        <>
          <p className="p-small">Challenge <code className="p-mono-chip">{challenge}</code></p>
          <div className="p-diagram">
            <div>
              <h3>Sign in this browser</h3>
              <p className="p-small p-muted">Paste the secret key; it stays in this tab’s memory.</p>
              <SecretKeyInput value={secret} onChange={setSecret} keys={s.keys} />
              <button type="button" className="p-btn" disabled={!match?.ok || act.busy} onClick={activate}>
                {act.busy ? <Spinner label="Signing & sending…" /> : 'Sign and activate'}
              </button>
            </div>
            <div>
              <h3>Or sign on your backend</h3>
              <CodeBlock code={activationSnippet(snippetValues(s), challenge)} label="activate.mjs" />
            </div>
          </div>
        </>
      )}
      <ErrorAlert error={get.error} />
      <ErrorBox error={act.error} />
      {(act.error as { code?: string } | null)?.code === 'unverified_domain' && (
        <Alert tone="warn">Production services activate only when their redirect URIs are on a verified domain of the organization. Verify the domain under Domains, then request a new challenge.</Alert>
      )}
      {result && (
        <Alert tone="ok">
          <strong>Service activated.</strong> <code>{result.serviceId}</code> is {result.status}. Pryme’s response signature was verified with Pryme’s verification key.
        </Alert>
      )}
    </div>
  );
}
